/**
 * KYC Service - Sumsub Integration
 * Handles KYC verification through Sumsub WebSDK
 */

import { apiClient } from "../utils/apiClient";
import logger from "../utils/logger";

class KYCService {
  constructor() {
    this.baseUrl = "/api/casino/kyc";
  }

  /**
   * Generate Sumsub access token for WebSDK
   * @param {string} levelName - Optional verification level
   * @returns {Promise<object>} Token data
   */
  async generateAccessToken(levelName = null) {
    try {
      // Only include levelName in request if it's not null
      const requestData = {};
      if (levelName !== null) {
        requestData.levelName = levelName;
      }

      const response = await apiClient.post(
        `${this.baseUrl}/token`,
        requestData,
      );

      // The KYC token endpoint returns data directly without success wrapper
      return response.data;
    } catch (error) {
      logger.error("KYC Service - Generate token error:", error);
      throw error;
    }
  }

  /**
   * Start a Didit KYC + AML verification session (the redemption-gated flow).
   * Returns the hosted Didit URL to redirect the player to. On completion the
   * signed Didit webhook maps kyc_status and autofills identity/address, and the
   * player returns via /kyc/callback?flow=kyc.
   * @returns {Promise<{url: string}>}
   */
  async startKycVerification() {
    try {
      const response = await apiClient.post(`${this.baseUrl}/start`, {});
      return response.data; // { url }
    } catch (error) {
      logger.error("KYC Service - Start verification error:", error);
      throw error;
    }
  }

  /**
   * Get current KYC verification status
   * @returns {Promise<object>} KYC status data
   */
  async getKYCStatus() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/status`);

      // The KYC endpoint returns data directly without success wrapper
      return response.data;
    } catch (error) {
      logger.error("KYC Service - Get status error:", error);
      throw error;
    }
  }

  /**
   * Check if KYC is required for a specific action
   * @param {string} action - Action to check (redemption, card_payment, etc.)
   * @returns {Promise<object>} KYC requirement data
   */
  async checkKYCRequired(action) {
    try {
      const response = await apiClient.get(
        `${this.baseUrl}/required/${action}`,
      );

      // The KYC required endpoint returns data directly without success wrapper
      return response.data;
    } catch (error) {
      logger.error("KYC Service - Check required error:", error);
      throw error;
    }
  }

  /**
   * Load Sumsub WebSDK script
   * @returns {Promise<void>}
   */
  async loadSumsubSDK() {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.snsWebSdk) {
        resolve();
        return;
      }

      // Check if script is already being loaded
      const existingScript = document.querySelector(
        'script[src*="websdk/build"]',
      );
      if (existingScript) {
        existingScript.addEventListener("load", resolve);
        existingScript.addEventListener("error", reject);
        return;
      }

      // Load the script
      const script = document.createElement("script");
      script.src =
        "https://static.sumsub.com/idensic/static/sns-websdk-builder.js";
      script.async = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error("Failed to load Sumsub WebSDK"));

      document.head.appendChild(script);
    });
  }

  /**
   * Initialize Sumsub WebSDK
   * @param {string} accessToken - Access token from backend
   * @param {string} containerId - DOM element ID to mount the SDK
   * @param {object} options - SDK configuration options
   * @returns {Promise<object>} SDK instance
   */
  async initializeSumsubSDK(accessToken, containerId, options = {}) {
    try {
      // Ensure SDK is loaded
      await this.loadSumsubSDK();

      if (!window.snsWebSdk) {
        throw new Error("Sumsub WebSDK not available");
      }

      const defaultOptions = {
        lang: "en",
        theme: "dark",
        uiConf: {
          customCss: `
            .sns-widget {
              background-color: #1A1D23 !important;
              color: #ECEEF2 !important;
            }
            .sns-widget .sns-button-primary {
              background-color: #F5B955 !important;
              border-color: #F5B955 !important;
            }
            .sns-widget .sns-button-primary:hover {
              background-color: #E5A945 !important;
              border-color: #E5A945 !important;
            }
          `,
        },

        onMessage: () => {},
        onError: (error) => {
          logger.error("Sumsub WebSDK error:", error);
        },
      };

      // Validate that accessToken is a string
      if (typeof accessToken !== "string" || !accessToken) {
        throw new Error(
          `Access token must be a non-empty string, got: ${typeof accessToken}`,
        );
      }

      const config = {
        ...defaultOptions,
        ...options,
      };

      // Create updateAccessToken callback
      const updateAccessToken = async () => {
        try {
          const tokenData = await this.generateAccessToken();
          return tokenData.token;
        } catch (error) {
          logger.error("Failed to refresh access token:", error);
          throw error;
        }
      };

      // Initialize SDK with access token and updateAccessToken callback
      const sdk = window.snsWebSdk
        .init(accessToken, updateAccessToken)
        .withConf(config)
        .build();

      return sdk;
    } catch (error) {
      logger.error("KYC Service - Initialize SDK error:", error);
      throw error;
    }
  }

  /**
   * Clean up Sumsub WebSDK
   * @param {string} containerId - DOM element ID that contained the SDK
   */
  cleanupSumsubSDK(containerId) {
    try {
      const container = document.getElementById(containerId);
      if (container) {
        container.innerHTML = "";
      }
    } catch (error) {
      logger.error("KYC Service - Cleanup SDK error:", error);
    }
  }

  /**
   * Get KYC status display information
   * @param {string} status - KYC status from backend
   * @returns {object} Display information
   */
  getStatusDisplay(status) {
    const statusMap = {
      verified: {
        label: "Verified",
        color: "text-green-400",
        bgColor: "bg-green-400/10",
        borderColor: "border-green-400/20",
        description: "Your identity has been successfully verified.",
      },
      not_verified: {
        label: "Not Verified",
        color: "text-muted-foreground",
        bgColor: "bg-muted/10",
        borderColor: "border-border/20",
        description:
          "Identity verification is required to access redemptions, and unlock all player bonuses.",
      },
    };

    return statusMap[status] || statusMap.not_verified;
  }
}

// Export singleton instance
export const kycService = new KYCService();
export default kycService;
